/**
 * Products Service
 *
 * Data access layer for product_types table.
 * Handles all product-related database operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { applyAccessControl, type AccessControl } from '../lib/accessControl';
import { mergeProductTranslation } from '../lib/translations';
import type { Database } from '../lib/database.types';

type ProductType = Database['public']['Tables']['product_types']['Row'];
type ProductTypeInsert = Database['public']['Tables']['product_types']['Insert'];
type ProductTypeUpdate = Database['public']['Tables']['product_types']['Update'];

/**
 * Extended product type with computed fields and relations
 */
export interface Product extends ProductType {
  location_name?: string;
  category_name?: string;
  inventory_count?: number;
  available_count?: number;
}

/**
 * Fetch all products with optional filters
 *
 * @param accessControl - User's access control info for merchant/location filtering
 * @param filters - Optional filters for products
 * @param language - Language code for translations (default: 'en')
 */
export async function getProducts(
  accessControl: AccessControl | null,
  filters?: {
    locationId?: string;
    categoryId?: string;
    active?: boolean;
    featured?: boolean;
    searchQuery?: string;
  },
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    let query = supabase
      .from('product_types')
      .select(`
        *,
        locations!inner(location_name, merchant_id),
        categories(category_name)
      `)
      .order('display_order', { ascending: true });

    // Apply access control filtering on joined locations table
    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    // Apply user filters
    if (filters?.locationId) {
      query = query.eq('location_id', filters.locationId);
    }

    if (filters?.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }

    if (filters?.active !== undefined) {
      query = query.eq('active', filters.active);
    }

    if (filters?.featured !== undefined) {
      query = query.eq('featured', filters.featured);
    }

    if (filters?.searchQuery) {
      query = query.or(`product_name.ilike.%${filters.searchQuery}%,description.ilike.%${filters.searchQuery}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Count inventory and fetch translations for each product
    const productsWithInventory = await Promise.all(
      (data || []).map(async (product) => {
        const { count: totalCount } = await supabase
          .from('inventory_items')
          .select('*', { count: 'exact', head: true })
          .eq('product_type_id', product.id);

        const { count: availableCount } = await supabase
          .from('inventory_items')
          .select('*', { count: 'exact', head: true })
          .eq('product_type_id', product.id)
          .eq('active', true);

        // Fetch and merge translation only for non-default languages.
        // Default language values are stored in the main table.
        // Defensive: skip merge when defaultLanguage is missing so callers
        // that forget to pass it don't accidentally pull in stale rows.
        let productWithTranslation = product;
        if (defaultLanguage && language !== defaultLanguage) {
          const { data: translation } = await supabase
            .from('product_type_translations')
            .select('*')
            .eq('product_type_id', product.id)
            .eq('language_code', language)
            .maybeSingle();

          productWithTranslation = mergeProductTranslation(product, translation);
        }

        return {
          ...productWithTranslation,
          location_name: (product.locations as any)?.location_name,
          category_name: (product.categories as any)?.category_name,
          inventory_count: totalCount || 0,
          available_count: availableCount || 0,
        };
      })
    );

    return { data: productsWithInventory, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch a single product by ID
 *
 * @param id - Product ID
 * @param accessControl - User's access control info for merchant/location filtering
 * @param language - Language code for translations (default: 'en')
 */
export async function getProductById(
  id: string,
  accessControl: AccessControl | null,
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    let query = supabase
      .from('product_types')
      .select(`
        *,
        locations!inner(location_name, pickup_address, location_contact, merchant_id),
        categories(category_name),
        inventory_items!product_type_id(*)
      `)
      .eq('id', id);

    // Apply access control filtering
    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    const { data, error } = await query.single();

    if (error) throw error;
    if (!data) return { data: null, error: null };

    // Skip translation merge for the default language —
    // default language values are stored in the main table
    if (defaultLanguage && language === defaultLanguage) {
      return { data, error: null };
    }

    // Fetch translation for non-default language
    const { data: translation } = await supabase
      .from('product_type_translations')
      .select('*')
      .eq('product_type_id', data.id)
      .eq('language_code', language)
      .maybeSingle();

    const productWithTranslation = mergeProductTranslation(data, translation);

    return { data: productWithTranslation, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new product
 */
export async function createProduct(product: ProductTypeInsert) {
  try {
    const { data, error } = await supabase
      .from('product_types')
      .insert(product)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an existing product
 */
export async function updateProduct(id: string, updates: ProductTypeUpdate) {
  try {
    const { data, error } = await supabase
      .from('product_types')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a product (soft delete by setting active to false)
 */
export async function deleteProduct(id: string) {
  try {
    // First check if this product has any inventory items
    const { data: inventoryItems, error: inventoryCheckError } = await supabase
      .from('inventory_items')
      .select('id')
      .eq('product_type_id', id)
      .limit(1);

    if (inventoryCheckError) throw inventoryCheckError;

    // If there are inventory items, prevent deletion
    if (inventoryItems && inventoryItems.length > 0) {
      return {
        data: null,
        error: 'Cannot delete this product because it has associated inventory items. Please deactivate it instead.'
      };
    }

    // No inventory items found, safe to delete
    const { error } = await supabase
      .from('product_types')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { data: null, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get pricing rules for a product
 */
export async function getProductPricing(productId: string, locationId?: string) {
  try {
    let query = supabase
      .from('pricing_rules')
      .select('*')
      .eq('product_type_id', productId)
      .order('duration_hours', { ascending: true });

    if (locationId) {
      query = query.eq('location_id', locationId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Upsert product type translation
 * Creates or updates a translation for a specific language
 */
export async function upsertProductTranslation(
  productTypeId: string,
  languageCode: string,
  translation: {
    product_name?: string | null;
    description?: string | null;
    variant_label?: string | null;
    variant_description_label?: string | null;
    features?: any | null;
    product_specific_info?: string | null;
  }
) {
  try {
    const { data, error } = await supabase
      .from('product_type_translations')
      .upsert(
        {
          product_type_id: productTypeId,
          language_code: languageCode,
          ...translation,
        },
        {
          onConflict: 'product_type_id,language_code',
        }
      )
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a product type translation row for a given language.
 *
 * Used to keep the invariant that default-language content lives only in the
 * main `product_types` table — any stale row in `product_type_translations`
 * for the default language should be cleaned up on save so it can't override
 * the freshly-updated main-table value on later reads.
 */
export async function deleteProductTranslation(productTypeId: string, languageCode: string) {
  try {
    const { error } = await supabase
      .from('product_type_translations')
      .delete()
      .eq('product_type_id', productTypeId)
      .eq('language_code', languageCode);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Get all translations for a product
 */
export async function getProductTranslations(productTypeId: string) {
  try {
    const { data, error } = await supabase
      .from('product_type_translations')
      .select('*')
      .eq('product_type_id', productTypeId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Copy options for the copyProduct function
 */
export interface CopyProductOptions {
  newName: string;
  targetLocationId: string;
  copyPricingRules: boolean;
  copyTranslations: boolean;
  copyInventoryItems: boolean;
}

/**
 * Copy an existing product to create a new one.
 * Optionally copies pricing rules, translations, and inventory items.
 *
 * @param sourceProductId - The ID of the product to copy
 * @param options - Copy configuration options
 */
export async function copyProduct(
  sourceProductId: string,
  options: CopyProductOptions
) {
  try {
    // 1. Fetch the source product
    const { data: source, error: fetchError } = await supabase
      .from('product_types')
      .select('*')
      .eq('id', sourceProductId)
      .single();

    if (fetchError) throw fetchError;
    if (!source) throw new Error('Source product not found');

    // 2. Create the new product with adjusted fields
    const newProduct: ProductTypeInsert = {
      product_name: options.newName,
      product_type: source.product_type,
      location_id: options.targetLocationId,
      description: source.description,
      model: source.model,
      category_id: source.category_id,
      picture_url: source.picture_url,
      sizes: source.sizes,
      features: source.features,
      maintenance_time_hours: source.maintenance_time_hours,
      pre_booking_hours: source.pre_booking_hours,
      inventory_type: source.inventory_type,
      pool_quantity: source.pool_quantity,
      cutoff_days_prior: source.cutoff_days_prior,
      cutoff_time: source.cutoff_time,
      bundle_components: source.bundle_components,
      delivery_eligible: source.delivery_eligible,
      variant_type: source.variant_type,
      variant_label: source.variant_label,
      variant_description_label: source.variant_description_label,
      product_specific_info: source.product_specific_info,
      display_order: (source.display_order || 0) + 1,
      active: true,
      featured: false,
      hidden: true,
      available_location_ids: null,
    };

    const { data: createdProduct, error: createError } = await supabase
      .from('product_types')
      .insert(newProduct)
      .select()
      .single();

    if (createError) throw createError;
    if (!createdProduct) throw new Error('Failed to create product copy');

    const newProductId = createdProduct.id;

    // 3. Copy translations if requested
    if (options.copyTranslations) {
      const { data: translations } = await supabase
        .from('product_type_translations')
        .select('*')
        .eq('product_type_id', sourceProductId);

      if (translations && translations.length > 0) {
        const translationCopies = translations.map((t) => ({
          product_type_id: newProductId,
          language_code: t.language_code,
          product_name: t.product_name,
          description: t.description,
          variant_label: t.variant_label,
          variant_description_label: t.variant_description_label,
          features: t.features,
          product_specific_info: t.product_specific_info,
        }));

        const { error: transError } = await supabase
          .from('product_type_translations')
          .insert(translationCopies);

        if (transError) throw transError;
      }
    }

    // 4. Copy pricing rules if requested (including their translations)
    if (options.copyPricingRules) {
      const { data: pricingRules } = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('product_type_id', sourceProductId);

      if (pricingRules && pricingRules.length > 0) {
        for (const rule of pricingRules) {
          const { id: oldRuleId, created_at, updated_at, ...ruleData } = rule;

          const { data: newRule, error: ruleError } = await supabase
            .from('pricing_rules')
            .insert({
              ...ruleData,
              product_type_id: newProductId,
            })
            .select()
            .single();

          if (ruleError) throw ruleError;

          // Copy pricing rule translations if product translations are also being copied
          if (newRule && options.copyTranslations) {
            const { data: ruleTranslations } = await supabase
              .from('pricing_rule_translations')
              .select('*')
              .eq('pricing_rule_id', oldRuleId);

            if (ruleTranslations && ruleTranslations.length > 0) {
              const ruleTransCopies = ruleTranslations.map((rt) => ({
                pricing_rule_id: newRule.id,
                language_code: rt.language_code,
                duration_label: rt.duration_label,
              }));

              await supabase
                .from('pricing_rule_translations')
                .insert(ruleTransCopies);
            }
          }
        }
      }
    }

    // 5. Copy inventory items if requested (with cleared/reset fields)
    if (options.copyInventoryItems) {
      const { data: inventoryItems } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('product_type_id', sourceProductId);

      if (inventoryItems && inventoryItems.length > 0) {
        for (const item of inventoryItems) {
          const {
            id: _oldItemId,
            created_at: _ca,
            updated_at: _ua,
            item_number: _itemNumber,
            usage_times: _ut,
            usage_hours: _uh,
            usage_revenue: _ur,
            combination_code: _cc,
            backup_code: _bc,
            last_maintenance_date: _lmd,
            next_maintenance_date: _nmd,
            maintenance_notes: _mn,
            // A barcode identifies one physical unit and is unique per merchant,
            // so the copy must not take it over (the insert would be rejected).
            barcode: _barcode,
            ...itemData
          } = item;

          await supabase
            .from('inventory_items')
            .insert({
              ...itemData,
              product_type_id: newProductId,
              item_number: '',
              usage_times: 0,
              usage_hours: 0,
              usage_revenue: 0,
              combination_code: null,
              backup_code: null,
              last_maintenance_date: null,
              next_maintenance_date: null,
              maintenance_notes: null,
            });
        }
      }
    }

    return { data: createdProduct, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
